import { docSyncLog, type DocSyncSession } from './docSync'
import { addWorkbookChangeHandlers, getWorkbookSnapshot } from './excel'

/**
 * Keeps the Excel workbook mirrored into a DocSyncSession. Unlike Word (where
 * the page-context snapshot already reads the full body, so mirroring rides
 * along), Excel re-reads every used range — far too heavy for selection
 * events. So this module owns its own change feed: workbook content events
 * (ExcelApi 1.9) when available, otherwise the caller's coarser triggers via
 * `requestRefresh` (debounced + serialized here either way).
 */

/** Mirror window caps — bound both the Office.js read and the synced bytes. */
const MAX_MIRROR_ROWS = 2000
const MAX_MIRROR_COLS = 100
const MAX_MIRROR_TOTAL_CELLS = 100000

const REFRESH_DEBOUNCE_MS = 500

export interface ExcelMirrorHandle {
  /** False when content-change events are unsupported — callers should then
   * invoke requestRefresh() from their own triggers (e.g. selection changes). */
  eventsSupported: boolean
  requestRefresh: () => void
  dispose: () => void
}

export function startExcelMirror(session: DocSyncSession): ExcelMirrorHandle {
  let disposed = false
  let timer: number | undefined
  let reading = false
  let pendingRefresh = false

  const refresh = async () => {
    if (disposed) return
    // Serialize reads: a change landing mid-read schedules one follow-up
    // instead of stacking concurrent whole-workbook reads.
    if (reading) {
      pendingRefresh = true
      return
    }
    reading = true
    try {
      const sheets = await getWorkbookSnapshot(MAX_MIRROR_ROWS, MAX_MIRROR_COLS, MAX_MIRROR_TOTAL_CELLS)
      if (!disposed) session.pushWorkbook(sheets)
    } catch (error) {
      if (import.meta.env.DEV) console.debug('[ramp-ai] excel mirror read failed:', error)
    } finally {
      reading = false
      if (pendingRefresh && !disposed) {
        pendingRefresh = false
        requestRefresh()
      }
    }
  }

  const requestRefresh = () => {
    if (disposed) return
    window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      void refresh()
    }, REFRESH_DEBOUNCE_MS)
  }

  const handle: ExcelMirrorHandle = {
    eventsSupported: false,
    requestRefresh,
    dispose: () => {
      disposed = true
      window.clearTimeout(timer)
    }
  }

  void refresh()
  void addWorkbookChangeHandlers(requestRefresh).then(({ supported, dispose }) => {
    if (disposed) {
      dispose()
      return
    }
    docSyncLog(
      supported
        ? 'excel content-change events active'
        : 'excel content-change events unavailable — re-mirroring on selection triggers'
    )
    handle.eventsSupported = supported
    const previousDispose = handle.dispose
    handle.dispose = () => {
      previousDispose()
      dispose()
    }
  })

  return handle
}
